import {
  DeliveryType,
  OfferT,
  ProductT,
  SkuT,
} from '@lambda/apis/src/catalog-v2/types';
import { CartLineT, NoteT } from '@lambda/apis/src/cart/types';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import settings from '@/settings';
import { ZipcodeStateData } from '@/lib/use-customer-zipcode';
import { GenerateAddOnsNotesT, GenerateProductNotesT } from './types';
import { generateDeliveryDateObjects } from './helpers';
import { generateBatteryHealthTitle } from '@/lib/collections/helpers';

export enum NoteNames {
  // Offer
  ID = 'ID',
  CATEGORY = '_category',
  OFFER_CATEGORY = '_offer_category',
  CATEGORY_PK = '_category_pk',
  // Add to Cart Date Time
  ADD_TO_CART_DATE_TIME = '_add_cart_date_time',
  // Delivery Dates
  DELIVERY_MIN = '_deliver_min',
  DELIVERY_MAX = '_deliver_max',
  CUTOFF_TIME = '_cutoff_time',
  CUTOFF_TIMEZONE = '_cutoff_timezone',
  DELIVER_STATE = '_deliver_state',
  DELIVER_ZIPCODE = '_deliver_zipcode',
  /** @deprecated use handlingTime */
  DISPATCH_MIN = '_dispatch_min',
  /** @deprecated use handlingTime */
  DISPATCH_MAX = '_dispatch_max',
  HANDLING_TIME = '_handling_time',
  COMPARE_AT_PRICE = '_compare_price',
  BATTERY_HEALTH = '_battery_health',
  // Shipping Type
  SHIPPING_TYPE = '_shipping_option',
  SHIPPING_OPTION_COST = '_shipping_option_cost',
  // For (Express Shipping + ReebeloCare)
  FOR = 'For',
  // For Cart Redirection
  PRODUCT_ID = '_product_id',
  SKU_ID = '_sku_id',
  // Cart + Checkout Summary
  OFFER_NAME = '_offer_name',
  OFFER_VARIANTS = '_offer_variants',
  // Type
  TYPE = '_type',
  // Cart line a recommendation was added under, so it can be cleaned up with it
  RECOMMENDED_FOR = '_recommended_for',
}

const addAddOnCategoryNotes = (category: ProductT['category']) => {
  // Early return if no category
  if (!category) {
    return null;
  }

  return {
    name: NoteNames.OFFER_CATEGORY,
    value: category.name.toString(),
  };
};

const addProductCategoryNotes = (category: ProductT['category']) => {
  const categoryNotes: NoteT[] = [];

  // Early return if no category
  if (!category) {
    return categoryNotes;
  }

  if (category.name) {
    categoryNotes.push({
      name: NoteNames.CATEGORY,
      value: category.name.toString(),
    });
  }

  if (category.pk) {
    categoryNotes.push({
      name: NoteNames.CATEGORY_PK,
      value: category.pk.toString(),
    });
  }

  return categoryNotes;
};

const addToCartDateTimeNote = (lineItem: CartLineT | null) => {
  const date = new Date().toISOString();

  const existingDate = lineItem?.notes?.find(
    (note) => note.name === NoteNames.ADD_TO_CART_DATE_TIME,
  )?.value;

  if (existingDate) {
    return {
      name: NoteNames.ADD_TO_CART_DATE_TIME,
      value: existingDate.toString(),
    };
  }

  return { name: NoteNames.ADD_TO_CART_DATE_TIME, value: date.toString() };
};

const addDeliveryNotes = (
  offer: OfferT | null,
  zipcodeStateData: ZipcodeStateData | null,
) => {
  const deliveryNotes = [];
  // Values

  const { standardMinDate, standardMaxDate } = generateDeliveryDateObjects(
    offer?.deliveryEstimates,
    zipcodeStateData?.state ?? null,
    zipcodeStateData?.region ?? null,
  );

  // Min Days
  if (standardMinDate) {
    deliveryNotes.push({
      name: NoteNames.DELIVERY_MIN,
      value: standardMinDate.toString(),
    });
  }

  // Max Days
  if (standardMaxDate) {
    deliveryNotes.push({
      name: NoteNames.DELIVERY_MAX,
      value: standardMaxDate.toString(),
    });
  }

  if (['reebelo-au', 'reebelo-nz', 'reebelo-ca'].includes(settings.store)) {
    if (zipcodeStateData?.state) {
      deliveryNotes.push({
        name: NoteNames.DELIVER_STATE,
        value: zipcodeStateData.state.toString(),
      });
    }

    if (zipcodeStateData?.zipcode) {
      deliveryNotes.push({
        name: NoteNames.DELIVER_ZIPCODE,
        value: zipcodeStateData.zipcode.toString(),
      });
    }
  }

  // Shipping Type + Cost Notes - express is chosen in the cart, so a
  // product line always starts on standard shipping.
  deliveryNotes.push({
    name: NoteNames.SHIPPING_TYPE,
    value: DeliveryType.STANDARD,
  });
  deliveryNotes.push({
    name: NoteNames.SHIPPING_OPTION_COST,
    value: '0',
  });
  const standardEstimate = offer?.deliveryEstimates?.find(
    (estimate) => estimate.type === DeliveryType.STANDARD,
  );

  if (standardEstimate) {
    const { cutoffTime, cutoffTimeZone, handlingTime } = standardEstimate;
    const handlingTimeStr = handlingTime?.toString() ?? '';

    deliveryNotes.push({
      name: NoteNames.CUTOFF_TIME,
      value: cutoffTime?.toString() ?? '',
    });
    deliveryNotes.push({
      name: NoteNames.CUTOFF_TIMEZONE,
      value: cutoffTimeZone?.toString() ?? '',
    });
    deliveryNotes.push({
      name: NoteNames.HANDLING_TIME,
      value: handlingTimeStr,
    });
  }

  return deliveryNotes;
};

const addCompareAtPriceNote = (compareAtPrice: number | undefined) => {
  if (compareAtPrice == null) {
    return null;
  }

  return {
    name: NoteNames.COMPARE_AT_PRICE,
    value: (compareAtPrice / 100).toString(),
  };
};

const addBatteryHealth = (sku: SkuT | null) => {
  const batteryHealth = sku?.variants?.find(
    (v) => v.variant.slug === 'batteryHealth',
  )?.value?.value;

  if (!batteryHealth) {
    return null;
  }

  return {
    name: NoteNames.BATTERY_HEALTH,
    value: batteryHealth.toString(),
  };
};

const addIdNote = (
  offer: OfferT | null,
  sku: SkuT | null,
  product: ProductT | null,
) => {
  // Product ID + SKU ID Notes are used to redirect users to the correct product page from the cart
  const ids = [];
  const vendorName = offer?.vendor?.id.split('#')?.[1] ?? '';

  const vendorSku = offer?.vendorSku ?? '';
  const skuId = sku?.id ?? '';
  const productId = product?.slug ?? '';

  ids.push({
    name: NoteNames.ID,
    value: `${settings.store}#${vendorName}#${vendorSku ?? skuId}`,
  });

  // SKU ID Note
  if (skuId) {
    ids.push({
      name: NoteNames.SKU_ID,
      value: skuId,
    });
  }

  // Product ID Note
  if (productId) {
    ids.push({
      name: NoteNames.PRODUCT_ID,
      value: productId,
    });
  }

  return ids;
};

const addForNote = (sku: SkuT | null, offer: OfferT | null) => {
  if (!sku) {
    return null;
  }

  const forNote = `[${offer?.vendorSku ?? sku.id}] ${sku.title}`;

  return {
    name: NoteNames.FOR,
    value: forNote.toString(),
  };
};

const addWarrantyNote = (selectedWarranty: WarrantyT | null) => {
  if (!selectedWarranty) {
    return null;
  }

  return {
    name: 'Duration',
    value: selectedWarranty.duration.toString(),
  };
};

const addOfferDetailsNotes = (product: ProductT, sku: SkuT | null) => {
  const offerNotes: { name: string; value: string }[] = [];

  if (!sku) {
    return offerNotes;
  }

  const productName = product.name;
  const carrier = sku.variants?.find((v) => v.variant.slug === 'carrier')?.value
    ?.value;
  let displayedCarrier = carrier;

  if (carrier === 'Fully Unlocked') {
    displayedCarrier = 'Unlocked';
  } else {
    displayedCarrier = `${carrier} - Locked`;
  }

  const productTitle = carrier
    ? `${productName} - ${displayedCarrier}`
    : productName;

  if (carrier && settings.store === 'reebelo-us') {
    offerNotes.push({
      name: NoteNames.OFFER_NAME,
      value: productTitle,
    });
  } else {
    offerNotes.push({
      name: NoteNames.OFFER_NAME,
      value: productTitle,
    });
  }

  if (sku.variants && sku.variants.length > 0) {
    const variantSlugs = (product?.variants ?? []).map((v) => v.variant.slug);

    // Follow Same order as Product Variants and only include variants that are displayed on PDP
    const extraVariants = variantSlugs
      .map((slug) => {
        if (slug === 'carrier') {
          return null;
        }

        const variant = sku?.variants.find(
          (v) => v.variant.slug === slug,
        )?.value;

        if (variant == null) {
          return null;
        }

        if (slug === 'batteryHealth') {
          return `${generateBatteryHealthTitle(variant?.value)} Battery`;
        }

        if (slug === 'condition') {
          return `${variant?.displayValue ?? variant?.value} Condition`;
        }

        return variant?.displayValue ?? variant?.value;
      })
      .filter((v): v is string => v != null && !productTitle.includes(v));

    offerNotes.push({
      name: NoteNames.OFFER_VARIANTS,
      value: extraVariants.join(' - '),
    });
  }

  return offerNotes;
};

const addTypeNote = (type: undefined | 'CART_RECOMMENDATION' | 'ACCESSORY') => {
  if (!type) {
    return null;
  }

  return {
    name: NoteNames.TYPE,
    value: type,
  };
};

const addRecommendedForNote = (recommendedFor: string | undefined) => {
  if (!recommendedFor) {
    return null;
  }

  return {
    name: NoteNames.RECOMMENDED_FOR,
    value: recommendedFor,
  };
};

export const generateProductNotes = ({
  product,
  sku,
  offer,
  lineItem,
  zipcodeStateData,
  type,
  recommendedFor,
}: GenerateProductNotesT) => {
  // Destructure needed values
  const compareAtPrice = sku?.compareAtPrice;

  const notes = [
    ...addIdNote(offer, sku, product),
    addToCartDateTimeNote(lineItem),
    ...addProductCategoryNotes(product.category),
    ...addDeliveryNotes(offer, zipcodeStateData),
    addCompareAtPriceNote(compareAtPrice),
    addBatteryHealth(sku),
    ...addOfferDetailsNotes(product, sku),
    addTypeNote(type),
    addRecommendedForNote(recommendedFor),
  ].filter(Boolean) as NoteT[];

  return notes;
};

export const generateAddOnsNotes = ({
  product,
  sku,
  offer,
  lineItem,
  selectedWarranty,
}: GenerateAddOnsNotesT) => {
  const notes = [
    ...addIdNote(offer, sku, product),
    addForNote(sku, offer),
    addToCartDateTimeNote(lineItem),
    addWarrantyNote(selectedWarranty),
    addAddOnCategoryNotes(product.category),
  ].filter(Boolean) as NoteT[];

  return notes;
};
